/**
 * HER ASKS ABOUT A HOME'S REGULAR CLEANS, where the home is drawn (round five, slice C3, ops's
 * section 12.4): under each home's regular cleans on Account, and under "Ask for a change" on Your
 * agreement, for a client alone; and since round six's slice S3, where they stand, with nothing to
 * press, under each home's line on Schedule (`CleansAskLines`). No artboard draws them; each line is Home's callout, each button
 * Schedule's outlined one inside a card, and the words are `CLEANS` in `words.ts`.
 *
 * - WHERE EACH ASK STANDS, the newest of each type the Worker gives (`cleansAsksView`): what was
 *   asked, by her or by the name the household lists, then sent, waiting for us to confirm, the
 *   office's own words or, where it sent none, that it acted, with what was asked said without its
 *   terms and the home's line above as what is in place (P1), or that it could not, or why it
 *   closed, ops's refusal in ops's words; never more than ops has said
 *   (CLAUDE.md binding rule 1), and never that anything has changed: the home's line is as ops last
 *   pushed it, and moves only when its push does. An ending is said as a cancel, "You asked to
 *   cancel house cleaning: every clean after Wednesday 4 November.", where she asked it as one: the
 *   ask itself says so (`cancel`), as the Worker read what its button said when she asked (the
 *   fifth client-journey walk's leftovers, 2026-10-20), on Account, Your agreement and Schedule
 *   alike, and still once ops has ended the agreement she asked to cancel and answered her, when
 *   the home has nothing live left to read it from (the review of those leftovers, 2026-10-20).
 *   Every other ending is said as an ending.
 * - WHAT SHE MAY ASK, a button for each the Worker offers, in its order, each opening its sheet
 *   (`CleansSheet.tsx`) over the page; an ask of its type that already waits offers none. Where an
 *   ending ends nothing that repeats, as at a home whose only live agreement is a one-time clean,
 *   its button names what it cancels, "Ask to cancel house cleaning" (`askWords`, the fifth
 *   client-journey walk, 2026-10-20), or the home's open visit of it by its kind and its day where
 *   the Worker gives one, "Ask to cancel the deep clean on Tuesday 27 October" (ops's answer on ops
 *   #76, 2026-10-22); where a repeating clean is listed, "Ask to end" as ever.
 * - THE PAGE HOLDS WHAT OUTLIVES A SHEET (`lib/asks.ts`): whether a request is out and its keys, for
 *   each home and ask, and which sheet is open. A request the door has written closes the sheet,
 *   says "Sent to the office." on the home until the page read again says more, and gives that line
 *   the focus, so it is read out; a sheet closed with nothing sent gives the focus back to its
 *   button, or to the page's heading where a read since took the button away. The door may answer
 *   after the sheet has closed: the home still says sent and the page is still read again, but the
 *   focus moves only where no other sheet has opened since. A sheet whose home a read since no
 *   longer holds, gone or not hers, as the door's 404 finds it, stays drawn on the home as it was,
 *   so what it says of that 404 is never taken away in silence (the owner, 2026-10-20).
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import {
  CLEANS_ASK_TYPES,
  type CleansAskShown,
  type CleansAskType,
  type HomeCleans,
} from '../api.ts';
import { askingFor, type Asking } from '../lib/asks.ts';
import { askWords } from '../lib/cleans.ts';
import { dayInWords } from '../lib/dates.ts';
import {
  ANSWERED,
  askedWithoutTerms,
  cadenceChoice,
  cancelAsked,
  changeAsked,
  CLEANS,
  endAsked,
  FAILED_BEFORE_PHONE,
  isScheduleRefusal,
  pauseAsked,
  REQUEST,
  resumeAsked,
  SCHEDULE_REFUSAL_WORDS,
  weekdayChoice,
  whoAsked,
} from '../words.ts';
import { CARD_BUTTON } from './buttons.ts';
import { CleansSheet } from './CleansSheet.tsx';
import { Phone } from './Phone.tsx';
import { focusPageHeading } from './RouteFocus.tsx';
import { SaidWithPhone } from './SaidWithPhone.tsx';

/** One home's one ask: the key what outlives a sheet is held under. */
const keyOf = (homeId: string, type: CleansAskType) => `${homeId} ${type}`;

/** A day in words, with its weekday, as Schedule says one, or null for one that is not real. */
const day = (value: string | undefined) => (value === undefined ? null : dayInWords(value));

/**
 * Whether ops accepted an ask with no words of the office's: then the office may have settled other
 * terms on a call with her (ops's 12.4 and 12.7), so her asked terms are not said beside it (P1).
 */
const actedOnUnsaid = (ask: CleansAskShown): boolean =>
  ask.state === 'accepted' && ask.answer === undefined;

/**
 * What was asked, in words, or null where the Worker gave too little to say it: with its terms,
 * but where ops accepted it with no words of the office's, without them (`askedWithoutTerms`), so
 * the days she asked are never read as the days the office set (P1). An ending she asked as a
 * cancel, as the ask itself says (`cancel`), is said as one (`cancelAsked`), with or without its
 * day, never read again from the home's agreements as they stand now.
 */
export function askedLine(ask: CleansAskShown): string | null {
  const who = whoAsked(ask.mine, ask.askedBy);
  const cancels = ask.type === 'service.end' && ask.cancel === true;
  if (actedOnUnsaid(ask)) {
    return cancels ? cancelAsked(who, ask.service) : askedWithoutTerms(who, ask.type, ask.service);
  }
  const from = day(ask.from);
  switch (ask.type) {
    case 'schedule.pause':
      return from === null ? null : pauseAsked(who, from, day(ask.until));
    case 'schedule.resume':
      return from === null ? null : resumeAsked(who, from);
    case 'schedule.change': {
      if (from === null) return null;
      const cadence = ask.cadence === undefined ? null : cadenceChoice(ask.cadence);
      const weekday = ask.weekday === undefined ? null : weekdayChoice(ask.weekday);
      return changeAsked(who, from, {
        ...(cadence === null ? {} : { cadence }),
        ...(weekday === null ? {} : { weekday }),
        ...(ask.mine && ask.window !== undefined ? { window: ask.window } : {}),
      });
    }
    case 'service.end': {
      const last = day(ask.lastDay);
      if (last === null) return null;
      return cancels ? cancelAsked(who, ask.service, last) : endAsked(who, ask.service, last);
    }
  }
}

/** Where an ask stands, in words, and whether the office's number follows; null for nothing to say. */
interface StateSaid {
  readonly words: string;
  /** The words carry the office's number, which is drawn as a link inside them. */
  readonly withNumber?: boolean;
  /** The office's number follows the words, and then a full stop. */
  readonly phone?: boolean;
}

/**
 * Where an ask stands: sent; waiting for us to confirm once ops has it; the office's own words once
 * it has decided, or, where it sent none, that it acted or could not, from ops's own state
 * (`ANSWERED`, round six, slice S2, and P1); closed, with why, where a line is written for the reason; ops's
 * refusal in ops's words, or that it did not go through. Null for a reason or a state no line is
 * written for: nothing is said rather than something wrong.
 */
export function stateSaid(ask: CleansAskShown): StateSaid | null {
  switch (ask.state) {
    case 'sent':
      return { words: REQUEST.sent };
    case 'received':
      return { words: REQUEST.waiting };
    case 'accepted':
      return { words: ask.answer ?? ANSWERED.actedOn };
    case 'declined':
      return ask.answer === undefined
        ? { words: ANSWERED.notDoneBeforePhone, phone: true }
        : { words: ask.answer };
    case 'cancelled':
      if (ask.reason === 'schedule_ended') return { words: CLEANS.closedEnded };
      if (ask.reason === 'withdrawn') return { words: REQUEST.withdrawn };
      if (ask.reason === 'membership_revoked' && !ask.mine) return { words: REQUEST.revoked };
      return null;
    case 'refused':
      return isScheduleRefusal(ask.code)
        ? { words: SCHEDULE_REFUSAL_WORDS[ask.code], withNumber: true }
        : { words: FAILED_BEFORE_PHONE, phone: true };
    case 'stopped':
      return { words: FAILED_BEFORE_PHONE, phone: true };
    default:
      return null;
  }
}

/** Home's callout: what was asked, and where it stands. Given a ref, it may take the focus. */
function Callout({
  type,
  asked,
  state,
  ref,
}: {
  readonly type: CleansAskType;
  readonly asked: string | null;
  readonly state: StateSaid;
  readonly ref?: Ref<HTMLDivElement> | undefined;
}) {
  return (
    <div
      ref={ref}
      tabIndex={ref === undefined ? undefined : -1}
      data-cleans-ask={type}
      className="mt-3 rounded-control border-2 border-border-brand bg-surface-accent-soft px-4 py-3"
    >
      <p className="text-small text-pretty text-text-body">
        {asked === null ? null : `${asked} `}
        {state.withNumber ? (
          <SaidWithPhone words={state.words} />
        ) : state.phone ? (
          <>
            {state.words} <Phone after="." />
          </>
        ) : (
          state.words
        )}
      </p>
    </div>
  );
}

/**
 * Where each of her household's asks about one home's regular cleans stands, one callout each in the
 * order a screen offers them, with nothing to press: what Schedule says under the home's line (round
 * six, slice S3, `docs/seam.md` P84), as this file says it on Account, so an ask she has just made
 * is never read as lost on the page she checks her cleans on. An ask with nothing to say is left out.
 */
export function CleansAskLines({ requests }: { readonly requests: readonly CleansAskShown[] }) {
  return (
    <>
      {CLEANS_ASK_TYPES.flatMap((type) => {
        const request = requests.find((ask) => ask.type === type);
        const state = request === undefined ? null : stateSaid(request);
        return request === undefined || state === null
          ? []
          : [<Callout key={type} type={type} asked={askedLine(request)} state={state} />];
      })}
    </>
  );
}

/** What a page does with her asks: the sheet over it, and each home's lines and buttons. */
export interface CleansAsking {
  /** The open sheet, for the page's shell to draw over it, or nothing. */
  readonly over: ReactNode | undefined;
  /** A home's lines and buttons, given the id of the home's name on the page, which names each button's home. */
  readonly block: (home: HomeCleans, describedBy?: string) => ReactNode;
}

interface Opened {
  readonly homeId: string;
  readonly type: CleansAskType;
  /**
   * The home as it was drawn when the sheet opened, which keeps the sheet drawn where a read since
   * no longer holds it, so what the sheet says of the door's 404 is never taken away in silence.
   */
  readonly seen: HomeCleans;
}

/**
 * The asks of a page about its homes' regular cleans: `homes` as the Worker gave them, the household
 * the page shows, its day, the way to read it again, and the way back to the door.
 */
export function useCleansAsks({
  homes,
  household,
  today,
  reload,
  onSignedOut,
}: {
  readonly homes: readonly HomeCleans[];
  readonly household: string;
  readonly today: string;
  readonly reload: () => void;
  readonly onSignedOut: () => void;
}): CleansAsking {
  /** The sheet open now, on which home and for which ask. */
  const [open, setOpen] = useState<Opened | null>(null);
  /** The same, as it is now, for a door's answer that comes after the sheet it was asked from. */
  const openNow = useRef<Opened | null>(null);
  /** Each request the door wrote from this page, by the home and the ask. */
  const [sentHere, setSentHere] = useState<ReadonlyMap<string, string>>(new Map());
  /** Where the focus goes once a sheet has gone: back to its button, or to what the home says. */
  const [focus, setFocus] = useState<{ readonly key: string; readonly to: 'button' | 'said' }>();
  const openers = useRef(new Map<string, HTMLButtonElement>());
  const said = useRef(new Map<string, HTMLDivElement>());
  /** Each home's and ask's asking, which outlives the sheet it was asked from (`lib/asks.ts`). */
  const asks = useRef(new Map<string, Asking>());

  // The line a request sent from here is said in stays the same element when the page is read again,
  // so the focus given it stays with it. A button a read since took away, its home gone or not hers
  // or its ask no longer offered, gives the page's heading the focus instead (`focusPageHeading`).
  useEffect(() => {
    if (focus === undefined) return;
    const target = (focus.to === 'button' ? openers : said).current.get(focus.key);
    if (target !== undefined) target.focus();
    else if (focus.to === 'button') focusPageHeading();
  }, [focus]);

  const openOn = (opened: Opened | null) => {
    openNow.current = opened;
    setOpen(opened);
  };

  const named = homes.length > 1;
  // The home the sheet is open on, as the page now holds it; where a read since the sheet opened
  // no longer holds it, gone or not hers, the sheet stays drawn on it as it was (the owner,
  // 2026-10-20).
  const home =
    open === null ? undefined : (homes.find((each) => each.id === open.homeId) ?? open.seen);
  const close = () => {
    if (open !== null) setFocus({ key: keyOf(open.homeId, open.type), to: 'button' });
    openOn(null);
  };
  const sent = (opened: Opened, requestId: string) => {
    const key = keyOf(opened.homeId, opened.type);
    setSentHere((held) => new Map(held).set(key, requestId));
    const now = openNow.current;
    if (now === null || (now.homeId === opened.homeId && now.type === opened.type)) {
      setFocus({ key, to: 'said' });
      openOn(null);
    }
    reload();
  };

  const over =
    open !== null && home !== undefined ? (
      <CleansSheet
        type={open.type}
        home={home}
        named={named}
        today={today}
        household={household}
        asking={askingFor(asks.current, keyOf(open.homeId, open.type))}
        onClose={close}
        onSent={(requestId) => sent(open, requestId)}
        onSignedOut={onSignedOut}
        onStale={reload}
      />
    ) : undefined;

  const block = (each: HomeCleans, describedBy?: string) => {
    const lines = CLEANS_ASK_TYPES.flatMap((type) => {
      const key = keyOf(each.id, type);
      const request = each.requests.find((ask) => ask.type === type);
      const here = sentHere.get(key);
      const ref = (node: HTMLDivElement | null) => {
        if (node) said.current.set(key, node);
        else said.current.delete(key);
      };
      // Sent from this page, and not yet in what the page read: it was sent, and nothing more.
      if (here !== undefined && request?.id !== here) {
        return [
          <Callout key={type} type={type} asked={null} state={{ words: REQUEST.sent }} ref={ref} />,
        ];
      }
      if (request === undefined) return [];
      const state = stateSaid(request);
      if (state === null) return [];
      return [
        <Callout key={type} type={type} asked={askedLine(request)} state={state} ref={ref} />,
      ];
    });
    const offered = CLEANS_ASK_TYPES.filter((type) => each.offers.includes(type));
    return (
      <>
        {lines}
        {offered.length === 0 ? null : (
          <div className="mt-3 flex flex-col gap-2">
            {offered.map((type) => {
              const key = keyOf(each.id, type);
              return (
                <button
                  key={type}
                  ref={(button) => {
                    if (button) openers.current.set(key, button);
                    else openers.current.delete(key);
                  }}
                  type="button"
                  aria-haspopup="dialog"
                  aria-describedby={describedBy}
                  onClick={() => {
                    setFocus(undefined);
                    openOn({ homeId: each.id, type, seen: each });
                  }}
                  className={CARD_BUTTON}
                >
                  {askWords(type, each)}
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return { over, block };
}
